import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { DataTestIds } from "@regrello/data-test-ids-api";
import {
  RegrelloIcon,
  RegrelloIconStyler,
  RegrelloLinkV2,
  RegrelloSpinner,
  RegrelloTypography,
} from "@regrello/ui-core";
import React from "react";
import { useCallback, useEffect, useState } from "react";

import { REGRELLO_PRIVACY_POLICY_URL } from "../../../../../constants/globalConstants";
import { RegrelloRestApiService } from "../../../../../services/RegrelloRestApiService";
import { useQueryMap } from "../../../../../utils/hooks/useQueryStrings";
import { RoutePaths, RouteQueryStringKeys } from "../../../../app/routes/consts";
import { RegrelloCopyright } from "../../../../atoms/copyright/RegrelloCopyright";

export const UnauthenticatedMFAResetPage = React.memo(function UnauthenticatedMFAResetPageFn() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const queryParams = useQueryMap();

  // Extract query parameters
  const email = decodeURIComponent(queryParams.email || "");
  const verificationUUID = decodeURIComponent(queryParams.verificationUUID || "");
  const timestamp = decodeURIComponent(queryParams.timestamp || "");

  const resetMFA = useCallback(async () => {
    if (
      email === "" ||
      verificationUUID === "" ||
      timestamp === "" ||
      email == null ||
      verificationUUID == null ||
      timestamp == null
    ) {
      setIsError(true);
      setErrorMessage(t`Something went wrong. Please check your email link and try again.`);
      setIsLoading(false);
      return;
    }

    try {
      const response = await RegrelloRestApiService.resetMfa(email, verificationUUID, timestamp);
      setIsLoading(false);
      if (response.json.reset) {
        setIsSuccess(true);
        // Redirect to login page after 10 seconds
        setTimeout(() => {
          window.location.replace(
            `${window.location.origin}${RoutePaths.LOGIN}?${new URLSearchParams({
              ...(email ? { [RouteQueryStringKeys.EMAIL]: email } : {}),
            }).toString()}`,
          );
        }, 10000);
      } else {
        setIsError(true);
        if (response.status === 400) {
          setErrorMessage(t`Something went wrong. Please check your email link and try again.`);
        } else if (response.status === 409) {
          setErrorMessage(t`Authentication has already been reset.`);
          setActionMessage(
            t`You can close this page and go back to sign in to set up MFA or request a new reset link.`,
          );
        } else {
          setErrorMessage(t`An unexpected error occurred. Please try again or contact support.`);
        }
      }
    } catch {
      setIsError(true);
      setIsSuccess(false);
      setIsLoading(false);
      setErrorMessage(t`An unexpected error occurred. Please try again or contact support.`);
    }
  }, [email, verificationUUID, timestamp]);

  const signInLinkHref = {
    pathname: RoutePaths.LOGIN,
    search: new URLSearchParams({
      ...(email ? { [RouteQueryStringKeys.EMAIL]: email } : {}),
    }).toString(),
  };

  useEffect(() => {
    void resetMFA();
  }, [resetMFA]);

  return (
    <div className="flex justify-center w-full h-full" data-testid={DataTestIds.UNAUTHENTICATED_MFA_RESET_PAGE_ROOT}>
      <div className="flex flex-col px-12 pb-4 w-150">
        <div className="flex flex-col justify-center flex-auto items-center">
          {isLoading && (
            <>
              <RegrelloSpinner className="mb-6" size="x-large" />
              <RegrelloTypography className="mb-10" variant="h2">
                {t`Resetting your Authentication`}
              </RegrelloTypography>
              <RegrelloTypography className="mb-4 text-center" variant="body">
                {t`Please wait while we process your request.`}
              </RegrelloTypography>
            </>
          )}

          {isSuccess && (
            <>
              <RegrelloIconStyler className="text-success-icon mb-6 scale-150" size="x-large">
                <RegrelloIcon iconName="complete" />
              </RegrelloIconStyler>
              <RegrelloTypography className="mb-10" variant="h2">
                {t`Authentication Reset Successful`}
              </RegrelloTypography>
              <RegrelloTypography className="mb-4 text-center" variant="body">
                {t`Your authentication has been successfully reset. You will need to re-enroll your device when you sign in.`}
              </RegrelloTypography>
              <RegrelloTypography className="mb-10 text-center" variant="body">
                {t`You'll be automatically redirected back to the sign in page. If you are not please click sign in below.`}
              </RegrelloTypography>
              <RegrelloLinkV2 className="mb-10 font-normal" to={signInLinkHref}>
                {t`Back to sign in`}
              </RegrelloLinkV2>
            </>
          )}

          {isError && (
            <>
              <RegrelloIconStyler className="text-danger-icon mb-6 scale-150" size="x-large">
                <RegrelloIcon iconName="alert" />
              </RegrelloIconStyler>
              <RegrelloTypography className="mb-10" variant="h2">
                {t`Authentication Reset Failed`}
              </RegrelloTypography>
              <RegrelloTypography className="mb-4 text-center" variant="body">
                {errorMessage || t`Unable to reset your authentication. Please try again or contact support.`}
              </RegrelloTypography>
              <RegrelloTypography className="mb-10 text-center" variant="body">
                {actionMessage || (
                  <Trans>
                    To request a new reset link, return to the sign in page and select <em>Reset MFA</em> after
                    providing your email address
                  </Trans>
                )}
              </RegrelloTypography>
              <RegrelloLinkV2 className="mb-10 font-normal" to={signInLinkHref}>
                {t`Back to sign in`}
              </RegrelloLinkV2>
            </>
          )}
        </div>
        <RegrelloTypography className="text-center flex justify-center justify-self-end" variant="body-xs">
          <a className="text-textDefault font-normal text-xs mr-6" href={REGRELLO_PRIVACY_POLICY_URL}>
            {t`Privacy Policy`}
          </a>
          <RegrelloCopyright />
        </RegrelloTypography>
      </div>
    </div>
  );
});
